/**
 * RUMI quiz — shared scoring + state contract.
 *
 * Depended on by BOTH sections/quiz-app.liquid and the quiz results template.
 * Pure functions only: no DOM, no network, no side effects. Anything that
 * touches the page belongs in rumi-quiz.js or rumi-quiz-results.js.
 *
 * Exposed as window.RumiQuiz.
 */
(function (global) {
  'use strict';

  /** Catalogue order — also the tie-break order when scores are equal. */
  var PRODUCTS = ['probiotics', 'hip-joint-support-chews', 'dog-dental-powder'];

  /**
   * The 21 concern chips from the quiz, in the order they appear in the design.
   * `id` is the stable slug used in URLs, sessionStorage and Klaviyo properties —
   * never re-slug from the label, or old links stop resolving.
   */
  var CONCERNS = [
    { id: 'itchy-skin', label: 'Itchy skin' },
    { id: 'allergies', label: 'Seasonal or environmental allergies' },
    { id: 'mobility-issues', label: 'Mobility issues' },
    { id: 'joint-discomfort', label: 'Joint discomfort' },
    { id: 'stiffness', label: 'Stiffness after walks or rest' },
    { id: 'anxiety', label: 'Anxiety or nervousness' },
    { id: 'separation-anxiety', label: 'Separation anxiety' },
    { id: 'runny-poops', label: 'Soft or runny poops' },
    { id: 'poor-gut-health', label: 'Poor gut health' },
    { id: 'excessive-shedding', label: 'Excessive shedding' },
    { id: 'dull-coat', label: 'Dull / dry coat' },
    { id: 'low-energy', label: 'Low energy / lethargy' },
    { id: 'wind-bloating', label: 'Wind or bloating' },
    { id: 'bad-breath', label: 'Bad breath' },
    { id: 'sensitive-stomach', label: 'Sensitive stomach' },
    { id: 'hyperactivity', label: 'Hyperactivity' },
    { id: 'picky-eater', label: 'Picky eater' },
    { id: 'licking-paws', label: 'Scratching / licking paws' },
    { id: 'slowing-down', label: 'Slowing down' },
    { id: 'low-immunity', label: 'Low immunity' },
    { id: 'hotspots', label: 'Hotspots' }
  ];

  /**
   * Concern -> product weights.
   *
   * The store carries three SKUs, so several concerns necessarily share one.
   * The anxiety cluster carries a weight of 1 toward Probiotics on a gut-brain
   * rationale — deliberately weak, so anxiety alone surfaces Probiotics but
   * never outranks a direct gut or joint match. Results copy for these must not
   * claim a calming effect.
   */
  var WEIGHTS = {
    'itchy-skin':         { 'probiotics': 3 },
    'allergies':          { 'probiotics': 3 },
    'mobility-issues':    { 'hip-joint-support-chews': 3 },
    'joint-discomfort':   { 'hip-joint-support-chews': 3 },
    'stiffness':          { 'hip-joint-support-chews': 3 },
    'anxiety':            { 'probiotics': 1 },
    'separation-anxiety': { 'probiotics': 1 },
    'runny-poops':        { 'probiotics': 3 },
    'poor-gut-health':    { 'probiotics': 3 },
    'excessive-shedding': { 'probiotics': 2 },
    'dull-coat':          { 'probiotics': 2 },
    'low-energy':         { 'probiotics': 2, 'hip-joint-support-chews': 1 },
    'wind-bloating':      { 'probiotics': 3 },
    'bad-breath':         { 'dog-dental-powder': 3, 'probiotics': 1 },
    'sensitive-stomach':  { 'probiotics': 3 },
    'hyperactivity':      { 'probiotics': 1 },
    'picky-eater':        { 'probiotics': 2 },
    'licking-paws':       { 'probiotics': 3 },
    'slowing-down':       { 'hip-joint-support-chews': 3 },
    'low-immunity':       { 'probiotics': 3 },
    'hotspots':           { 'probiotics': 3 }
  };

  /** A product joins the bundle at this score or above. */
  var THRESHOLD = 2;

  function concernById(id) {
    for (var i = 0; i < CONCERNS.length; i++) {
      if (CONCERNS[i].id === id) return CONCERNS[i];
    }
    return null;
  }

  /**
   * Score the selected concerns against the catalogue.
   * @param {string[]} concerns - concern ids
   * @returns {Object} handle -> score, every product present (0 if unscored)
   */
  function scoreAnswers(concerns) {
    var scores = {};
    PRODUCTS.forEach(function (handle) { scores[handle] = 0; });

    (concerns || []).forEach(function (id) {
      var row = WEIGHTS[id];
      if (!row) return;
      Object.keys(row).forEach(function (handle) {
        if (handle in scores) scores[handle] += row[handle];
      });
    });

    return scores;
  }

  /**
   * Turn scores into the ordered bundle shown on the results page.
   *
   * - Everything at or above THRESHOLD, ranked high to low.
   * - If nothing clears it but something scored, take the single best match,
   *   so an anxiety-only answer still yields one product rather than the
   *   full routine.
   * - If nothing scored at all (no concerns picked), recommend the whole routine.
   *
   * Ties resolve by catalogue order, so the same answers always give the
   * same bundle in the same order.
   *
   * @param {string[]} concerns - concern ids
   * @returns {{handles: string[], scores: Object, isFallback: boolean}}
   */
  function recommend(concerns) {
    var scores = scoreAnswers(concerns);

    var ranked = PRODUCTS.slice().sort(function (a, b) {
      if (scores[b] !== scores[a]) return scores[b] - scores[a];
      return PRODUCTS.indexOf(a) - PRODUCTS.indexOf(b);
    });

    var qualified = ranked.filter(function (h) { return scores[h] >= THRESHOLD; });
    if (qualified.length) {
      return { handles: qualified, scores: scores, isFallback: false };
    }

    if (scores[ranked[0]] > 0) {
      return { handles: [ranked[0]], scores: scores, isFallback: false };
    }

    return { handles: PRODUCTS.slice(), scores: scores, isFallback: true };
  }

  /**
   * Answers -> URL query string.
   *
   * Plain readable params rather than an encoded blob, so a Klaviyo email
   * template can rebuild this link from profile properties. Email is
   * deliberately never included.
   *
   * @param {Object} answers - {name, weightKg, dob, breed, concerns[]}
   * @returns {string} query string without the leading "?"
   */
  function encodeState(answers) {
    var a = answers || {};
    var parts = [];
    function add(key, value) {
      if (value === undefined || value === null || value === '') return;
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    }
    add('name', a.name);
    add('weight', a.weightKg);
    add('dob', a.dob);
    add('breed', a.breed);
    if (a.concerns && a.concerns.length) add('concerns', a.concerns.join(','));
    return parts.join('&');
  }

  /**
   * URL query string -> answers. Unknown concern ids are dropped rather than
   * trusted, so a hand-edited link can't inject arbitrary text into the page.
   *
   * @param {string} search - location.search, with or without "?"
   * @returns {Object} {name, weightKg, dob, breed, concerns[]}
   */
  function decodeState(search) {
    var query = (search || '').replace(/^\?/, '');
    var out = { name: '', weightKg: null, dob: '', breed: '', concerns: [] };
    if (!query) return out;

    var params = {};
    query.split('&').forEach(function (pair) {
      if (!pair) return;
      var idx = pair.indexOf('=');
      var key = idx === -1 ? pair : pair.slice(0, idx);
      var val = idx === -1 ? '' : pair.slice(idx + 1);
      try {
        params[decodeURIComponent(key)] = decodeURIComponent(val.replace(/\+/g, ' '));
      } catch (e) { /* skip malformed pairs */ }
    });

    out.name = (params.name || '').slice(0, 40);
    out.breed = (params.breed || '').slice(0, 60);
    out.dob = /^\d{4}-\d{2}-\d{2}$/.test(params.dob || '') ? params.dob : '';

    var weight = parseFloat(params.weight);
    out.weightKg = isFinite(weight) && weight > 0 && weight <= 120 ? weight : null;

    if (params.concerns) {
      out.concerns = params.concerns.split(',').filter(function (id) {
        return !!concernById(id);
      });
    }

    return out;
  }

  /** kg -> lb, matching the "30 kg | 66.14 lb" readout in the design. */
  function toPounds(kg) {
    return Math.round(kg * 2.2046226218 * 100) / 100;
  }

  /**
   * Age in whole years and months, for the results recap card.
   * Returns null for a missing or future date of birth.
   */
  function ageFrom(dob, now) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob || '')) return null;
    var born = new Date(dob + 'T00:00:00');
    var today = now || new Date();
    if (isNaN(born.getTime()) || born > today) return null;

    var months = (today.getFullYear() - born.getFullYear()) * 12 +
                 (today.getMonth() - born.getMonth());
    if (today.getDate() < born.getDate()) months -= 1;
    if (months < 0) months = 0;

    return { years: Math.floor(months / 12), months: months % 12 };
  }

  global.RumiQuiz = {
    PRODUCTS: PRODUCTS,
    CONCERNS: CONCERNS,
    WEIGHTS: WEIGHTS,
    THRESHOLD: THRESHOLD,
    STORAGE_KEY: 'rumi.quiz.v1',
    concernById: concernById,
    scoreAnswers: scoreAnswers,
    recommend: recommend,
    encodeState: encodeState,
    decodeState: decodeState,
    toPounds: toPounds,
    ageFrom: ageFrom
  };
})(window);
